/* Badkamervoorjou: explicit installation parts. One cart POST; never auto-select paid extras.
 * Only mapped trim forms are intercepted. Other products keep the original theme controller.
 */
(() => {
  'use strict';
  const money = (cents, currency = 'EUR') => new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', { style: 'currency', currency }).format(cents / 100);
  const text = (root, selector, value) => { const node = root.querySelector(selector); if (node && node.textContent !== value) node.textContent = value; };

  class BvjInbouwChoice extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      const json = this.querySelector('[data-bvj-inbouw-data]');
      if (!json) return;
      try { this.data = JSON.parse(json.textContent); } catch (_) { return; }
      this.form = this.closest('form');
      this.productInfo = this.closest('product-info');
      this.nativeForm = this.closest('product-form');
      this.fieldset = this.querySelector('fieldset');
      if (!this.form || !this.fieldset || !this.data.part?.id) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.fieldset.disabled = false;
      this.querySelectorAll('input[type="radio"]').forEach(input => { input.required = true; });
      this.schedule = () => { cancelAnimationFrame(this.frame); this.frame = requestAnimationFrame(() => this.sync()); };
      this.form.addEventListener('submit', event => this.onSubmit(event), { capture: true, signal });
      document.addEventListener('change', event => {
        if (event.target.form === this.form || this.productInfo?.contains(event.target)) {
          this.showError('');
          this.schedule();
        }
      }, { signal });
      document.addEventListener('input', event => { if (event.target.form === this.form) this.schedule(); }, { signal });
      this.observer = new MutationObserver(this.schedule);
      const idInput = this.form.querySelector('[name="id"]');
      if (idInput) this.observer.observe(idInput, { attributes: true, attributeFilter: ['value', 'disabled'] });
      this.sync();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
    }

    getSummary() {
      if (!this.form || !this.data) return null;
      const id = this.form.querySelector('[name="id"]')?.value;
      let variant = this.data.variants.find(item => String(item.id) === String(id));
      try {
        const current = JSON.parse(this.productInfo?.querySelector('variant-selects [data-selected-variant]')?.textContent || 'null');
        if (current && String(current.id) === String(id) && Number.isFinite(current.price)) variant = { ...variant, ...current };
      } catch (_) { /* The server-rendered variant table remains a safe fallback. */ }
      const quantityInput = this.form.elements.namedItem('quantity') || this.productInfo?.querySelector('.quantity__input');
      const quantity = quantityInput ? Number(quantityInput.value) : 1;
      const choice = this.querySelector('input[name="bvj_inbouw_choice"]:checked')?.value || '';
      const add = choice === 'yes';
      const part = this.data.parts ? this.data.parts[variant?.sku?.slice(0, 1)] : this.data.part;
      if (!part) return null;
      const validQuantity = Number.isSafeInteger(quantity) && quantity > 0;
      const partQuantityValid = validQuantity && quantity >= (part.min || 1) && (!part.max || quantity <= part.max) && (quantity - (part.min || 1)) % (part.increment || 1) === 0;
      const valid = !!variant && variant.available !== false && validQuantity && (!add || (part.available && partQuantityValid));
      const unitPrice = variant ? variant.price + (add ? part.price : 0) : null;
      return { choice, add, valid, quantity, variant, part, kind: this.data.kind, currency: this.data.currency, unitPrice, totalPrice: valid && unitPrice !== null ? unitPrice * quantity : null };
    }

    sync() {
      const summary = this.getSummary();
      if (!summary) return;
      const label = `${summary.add ? 'Afbouwdeel + inbouwdeel' : 'Afbouwdeel'}, ${Number.isSafeInteger(summary.quantity) ? summary.quantity : '—'} ${summary.quantity === 1 ? 'stuk' : 'stuks'}`;
      text(this, '[data-bvj-inbouw-total-label]', label);
      text(this, '[data-bvj-inbouw-total]', summary.totalPrice === null ? '—' : money(summary.totalPrice, summary.currency));
      if (!this.busy) text(this, '[data-bvj-inbouw-status]', !summary.choice ? 'Kies hierboven of je het inbouwdeel nodig hebt.' : summary.add ? 'Beide artikelen worden als aparte regels aan je winkelwagen toegevoegd.' : 'Je bestelt alleen het afbouwdeel. Het inbouwdeel is niet inbegrepen.');
      if (summary.kind === 'sunshower') {
        text(this, '[data-bvj-inbouw-total-label]', `${summary.add ? 'Sunshower + installatiebox' : 'Sunshower'}, ${summary.quantity} ${summary.quantity === 1 ? 'stuk' : 'stuks'}`);
        text(this, '[data-bvj-box-title]', summary.part.title);
        text(this, '[data-bvj-box-sku]', summary.part.sku);
        text(this, '[data-bvj-box-price]', money(summary.part.price, summary.currency));
        text(this, '[data-bvj-box-extra]', `+ ${money(summary.part.price, summary.currency)}`);
        const yes = this.querySelector('input[value="yes"]');
        if (yes) yes.disabled = !summary.part.available;
        const picture = this.querySelector('[data-bvj-box-image]');
        if (picture && summary.part.image) { picture.src = summary.part.image; picture.alt = summary.part.title; }
        const link = this.querySelector('[data-bvj-box-title]');
        if (link) link.href = summary.part.url;
        if (!this.busy) text(this, '[data-bvj-inbouw-status]', !summary.choice ? 'Kies of je de bijpassende installatiebox wilt meebestellen.' : summary.add ? 'De Sunshower en de box voor jouw maat worden apart aan je winkelwagen toegevoegd.' : 'Je bestelt alleen de Sunshower, zonder installatiebox.');
      }
    }

    showError(message) {
      const node = this.querySelector('[data-bvj-inbouw-error]');
      if (node) { node.textContent = message; node.hidden = !message; }
    }

    focusChoice() {
      const target = this.querySelector('input[type="radio"]:not(:disabled)');
      this.scrollIntoView({ block: 'center', behavior: 'auto' });
      target?.focus({ preventScroll: true });
      target?.reportValidity();
    }

    onSubmit(event) {
      const summary = this.getSummary();
      if (!summary) return;
      const button = this.form.querySelector('button[name="add"][type="submit"]');
      if (this.busy || button?.disabled || button?.getAttribute('aria-disabled') === 'true') {
        event.preventDefault(); event.stopImmediatePropagation(); return;
      }
      if (!summary.choice || !summary.valid || !this.form.checkValidity()) {
        event.preventDefault(); event.stopImmediatePropagation();
        this.showError(!summary.choice ? (summary.kind === 'sunshower' ? 'Kies of je de installatiebox wilt meebestellen.' : 'Kies of je het inbouwdeel wilt meebestellen.') : 'Controleer het aantal en de beschikbaarheid van je gekozen combinatie.');
        if (!summary.choice) this.focusChoice();
        else {
          const invalid = [...this.form.elements].find(input => input.willValidate && !input.validity.valid);
          (invalid || this).scrollIntoView({ block: 'center', behavior: 'auto' });
          invalid?.focus({ preventScroll: true }); invalid?.reportValidity();
        }
        return;
      }
      if (!summary.add) return; // The native, unchanged product-form submits the trim only.
      event.preventDefault(); event.stopImmediatePropagation();
      this.addCombination(summary, button);
    }

    buildPayload(summary, cart) {
      const source = new FormData(this.form);
      const parent = { id: Number(summary.variant.id), quantity: summary.quantity };
      const properties = {};
      for (const [name, value] of source) {
        const match = /^properties\[(.+)\]$/.exec(name);
        if (match && typeof value === 'string') properties[match[1]] = value;
      }
      properties[summary.kind === 'sunshower' ? 'Bijpassende installatiebox' : 'Bijpassend inbouwdeel'] = summary.part.sku;
      parent.properties = properties;
      const sellingPlan = source.get('selling_plan');
      if (sellingPlan) parent.selling_plan = sellingPlan;
      const part = { id: Number(summary.part.id), quantity: summary.quantity, properties: { 'Voor artikelnummer': summary.variant.sku || String(summary.variant.id) } };
      const body = { items: [parent, part] };
      if (cart) {
        body.sections = cart.getSectionsToRender().map(section => section.id);
        body.sections_url = window.location.pathname;
      }
      return body;
    }

    async addCombination(summary, button) {
      this.busy = true;
      this.showError('');
      this.nativeForm?.handleErrorMessage?.();
      button?.setAttribute('aria-disabled', 'true');
      button?.classList.add('loading');
      const spinner = button?.querySelector('.loading__spinner');
      spinner?.classList.remove('hidden');
      this.fieldset.disabled = true;
      text(this, '[data-bvj-inbouw-status]', summary.kind === 'sunshower' ? 'Sunshower en installatiebox worden toegevoegd…' : 'Afbouwdeel en inbouwdeel worden toegevoegd…');
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      cart?.setActiveElement?.(document.activeElement);
      const body = this.buildPayload(summary, cart);
      const cartUrl = window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
      const addUrl = window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`;
      let confirmed = false;
      let deferred;
      try {
        const EventClass = window.StandardEvents?.CartLinesUpdateEvent;
        if (EventClass) {
          deferred = EventClass.createPromise();
          deferred.promise.catch(() => {});
          this.dispatchEvent(new EventClass({ action: 'add', context: 'product', lines: body.items.map(item => ({ merchandiseId: String(item.id), quantity: item.quantity })), promise: deferred.promise }));
        }
        const response = await fetch(addUrl, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(body) });
        const data = await response.json();
        if (!response.ok || data.status) {
          const message = typeof data.description === 'string' ? data.description : 'De combinatie kon niet worden toegevoegd. Controleer je winkelwagen en probeer het opnieuw.';
          deferred?.reject(new Error(message));
          this.showError(message);
          this.nativeForm?.dispatchCartErrorEvent?.(message, 'INVALID');
          return;
        }
        confirmed = true;
        this.nativeForm?.resolveCartLinesUpdate?.(deferred);
        text(this, '[data-bvj-inbouw-status]', `${summary.kind === 'sunshower' ? 'Sunshower' : 'Afbouwdeel'} en ${summary.part.sku} zijn toegevoegd aan je winkelwagen.`);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: String(summary.variant.id), cartData: data })).catch(() => {});
        }
        if (!cart || !body.sections.every(id => typeof data.sections?.[id] === 'string' && data.sections[id])) {
          window.location.assign(cartUrl); return;
        }
        const mainItem = data.items?.find(item => String(item.variant_id || item.id) === String(summary.variant.id)) || data.items?.[0];
        const state = { ...data, ...(mainItem || {}), sections: data.sections, items: data.items };
        const render = () => {
          cart.classList.remove('is-empty');
          cart.renderContents(state);
          if (cart.tagName.toLowerCase() === 'cart-notification') {
            const documentFragment = new DOMParser().parseFromString(data.sections['cart-notification-product'], 'text/html');
            const partItem = data.items?.find(item => String(item.variant_id || item.id) === String(summary.part.id));
            const partMarkup = partItem && documentFragment.getElementById(`cart-notification-product-${partItem.key}`);
            const target = document.getElementById('cart-notification-product');
            if (partMarkup && target) {
              const extra = document.createElement('div'); extra.className = 'bvj-inbouw__added-extra'; extra.innerHTML = partMarkup.innerHTML; target.appendChild(extra);
            }
          }
        };
        const modal = this.closest('quick-add-modal');
        if (modal) {
          document.body.addEventListener('modalClosed', () => setTimeout(() => { try { render(); } catch (_) { window.location.assign(cartUrl); } }), { once: true });
          modal.hide(true);
        } else render();
      } catch (error) {
        if (confirmed) { window.location.assign(cartUrl); return; }
        deferred?.reject(error);
        this.showError('We kunnen niet bevestigen of beide artikelen zijn toegevoegd. Controleer je winkelwagen voordat je opnieuw bestelt.');
        this.nativeForm?.dispatchCartErrorEvent?.(error.message || 'Network error', 'SERVICE_UNAVAILABLE');
      } finally {
        this.busy = false;
        this.fieldset.disabled = false;
        button?.classList.remove('loading');
        button?.removeAttribute('aria-disabled');
        spinner?.classList.add('hidden');
        if (!confirmed) this.sync();
      }
    }
  }

  class BvjPartsGrid extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.cards = [...this.querySelectorAll(':scope > ul > li.grid__item')];
      if (!this.cards.length && Number(this.dataset.pages) > 0 && Number(this.dataset.current) > Number(this.dataset.pages)) {
        const url = new URL(window.location.href); url.searchParams.delete('page'); window.location.replace(url); return;
      }
      this.more = this.querySelector('[data-bvj-grid-more]');
      this.status = this.querySelector('[data-bvj-grid-status]');
      this.batch = Math.max(8, Number(this.dataset.batch) || 16);
      this.limit = this.batch;
      this.controller = new AbortController();
      this.more?.addEventListener('click', () => {
        const firstNew = this.cards[this.limit];
        this.limit += this.batch; this.sync();
        const link = firstNew?.querySelector('a');
        link?.focus({ preventScroll: true });
      }, { signal: this.controller.signal });
      this.sync();
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
    sync() {
      this.cards.forEach((card, index) => { card.hidden = index >= this.limit; });
      const toolbar = this.querySelector('.bvj-parts-grid__more');
      if (toolbar) toolbar.hidden = this.cards.length <= this.limit;
      if (this.status) this.status.textContent = `${Math.min(this.limit, this.cards.length)} van ${this.cards.length} producten getoond`;
      if (this.more) this.more.textContent = `Toon ${Math.min(this.batch, Math.max(0, this.cards.length - this.limit))} producten meer`;
    }
  }
  if (!customElements.get('bvj-inbouw-choice')) customElements.define('bvj-inbouw-choice', BvjInbouwChoice);
  if (!customElements.get('bvj-parts-grid')) customElements.define('bvj-parts-grid', BvjPartsGrid);
})();
